/**
 * `value`, once it has stopped changing for `ms`.
 *
 * The two studio price builders **and the client's own**. `PackageBuilder`'s comment
 * argued against extracting this while there was one of them — "the only thing in this portal
 * that fires a request per keystroke is a price builder, and a shared `useDebounce`
 * would invite every other control to become one" — and `QuoteBuilder` is the second,
 * so here it is, still in `components/studio/` rather than in a `hooks/` directory
 * that would say it is for anybody.
 *
 * What it is for: a block count and an add-on quantity are typed, every keystroke is a
 * `POST /api/studio/catalog_compute_price`, and forty calls to compose one package is
 * forty rows in an access log for one decision. It is **not** what stops a stale
 * answer being rendered — `useApiQuery` keys on the input, so the answer to a package
 * that is no longer on screen belongs to a different cache entry and cannot be shown.
 */

import { useEffect, useState } from 'react'

/**
 * How long a price builder waits after the last keystroke.
 *
 * **One number.** There were two constants of this name with two values — 400 here
 * and 250 in `pages/client/billing/useIndicativePrice.ts` — which is the shape of
 * duplication that reads as a decision and is an accident. 250 is the one that was
 * measured against a real keystroke rate on the client's own package picker, so it is
 * the one that survives.
 */
export const PRICE_DEBOUNCE_MS = 250

export function useDebounced<T>(value: T, ms: number = PRICE_DEBOUNCE_MS): T {
  const [settled, setSettled] = useState(value)

  useEffect(() => {
    /*
     * **The first value is taken as it stands**, and every equal one after it costs
     * nothing: a page arriving at `?add=business_consulting` should price it rather
     * than wait a quarter of a second to decide to, and a re-render that produced the
     * same value should not restart the timer. `Object.is` rather than `===` because
     * the value is sometimes an array identity and sometimes a number.
     */
    if (Object.is(settled, value)) return undefined
    const timer = setTimeout(() => {
      setSettled(value)
    }, ms)
    return () => {
      clearTimeout(timer)
    }
  }, [value, ms, settled])

  return settled
}
